import {
  Button,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/_components/ui'
import { formatPhoneNumber } from '@/_helpers/format-phone-number'
import type { TExposedFacilityDTO } from '@doutorsim/shared/exposed/facilities'
import { formatBRLFromCents } from '@doutorsim/shared/helpers/currency'
import { Link } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { ExternalLink } from 'lucide-react'
import * as React from 'react'
import { ReleaseHandoffButton } from '../applications/_components/release-handoff-button'
import { getStatusConfig } from '../_constants/admission-status'
import { getStatusAction } from '../_constants/status-actions'
import { formatDate } from '../_helpers/date'
import { getAdmissionFormStatusLabel } from '../_helpers/get-admission-form-status-label'
import type { TStaffApplicationListItem } from '../_lib/api/entities/applications.staff'
import type { TUserType } from '../_lib/auth-client.types'
import { useCompanyStore } from '../_stores'

type TApplicationRow = TStaffApplicationListItem & {
  facility?: TExposedFacilityDTO | null
}

type RowData = {
  original: TApplicationRow
}

type LinkResult = {
  to: string
  params: Record<string, string>
  search?: Record<string, string>
}

export type LinkBuilder = (row: RowData, subpath?: string) => LinkResult

const companyLink: LinkBuilder = (row, subpath = 'profile') => {
  const companySlug = useCompanyStore.getState().company?.slug
  return {
    to: `/companies/${companySlug ?? row.original.companyId}/applications/${row.original.id}/${subpath}` as any,
    params: {},
  }
}

const staffLink: LinkBuilder = (row, subpath = 'profile') => ({
  to: `/applications/$applicationId/${subpath}` as any,
  params: { applicationId: row.original.id },
  search: {
    companyId: row.original.companyId,
    facilityId: row.original.facilityId,
  },
})

export function createFacilityLink(companySlug: string, facilitySlug: string): LinkBuilder {
  return (row, subpath = 'profile') => ({
    to: `/companies/${companySlug}/facilities/${facilitySlug}/applications/${row.original.id}/${subpath}` as any,
    params: {},
  })
}

const ApplicationLink = React.forwardRef<
  HTMLAnchorElement,
  {
    row: RowData
    getLink: LinkBuilder
    children: React.ReactNode
    className?: string
  } & Omit<React.ComponentPropsWithoutRef<'a'>, 'children'>
>(function ApplicationLink({ row, getLink, children, className, ...rest }, ref) {
  const { to, params, search } = getLink(row)
  return (
    <Link ref={ref} to={to} params={params} search={search} className={className} {...rest}>
      {children}
    </Link>
  )
})

function buildColumns(getLink: LinkBuilder, userType: TUserType) {
  const nameColumn: ColumnDef<TApplicationRow> = {
    accessorKey: 'name',
    header: 'Nome',
    meta: { width: '25%' },
    cell: ({ row }) => (
      <ApplicationLink row={row} getLink={getLink} className="inline-flex w-full px-8 py-3">
        <span>{row.original.person?.name}</span>
      </ApplicationLink>
    ),
  }

  const phoneColumn: ColumnDef<TApplicationRow> = {
    accessorKey: 'phone',
    header: 'Telefone',
    meta: { width: '20%' },
    cell: ({ row }) => (
      <ApplicationLink row={row} getLink={getLink} className="inline-flex w-full px-8 py-3">
        <span>
          {row.original.person?.phone
            ? formatPhoneNumber(row.original.person?.phone)?.formatNational()
            : '-'}
        </span>
      </ApplicationLink>
    ),
  }

  const facilityColumn: ColumnDef<TApplicationRow> = {
    accessorKey: 'facility.name',
    header: 'Instituição',
    meta: { width: '20%' },
    cell: ({ row }) => (
      <ApplicationLink row={row} getLink={getLink} className="inline-flex w-full px-8 py-3">
        {row.original.facility ? (
          <span>{row.original.facility.name}</span>
        ) : (
          <Skeleton className="h-4 w-24" />
        )}
      </ApplicationLink>
    ),
  }

  const statusColumn: ColumnDef<TApplicationRow> = {
    accessorKey: 'admissionForm.status',
    header: 'Status',
    meta: { width: '20%' },
    cell: ({ row }) => {
      const { status } = row.original
      const config = getStatusConfig(status, userType)

      return (
        <TooltipProvider>
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <ApplicationLink
                row={row}
                getLink={getLink}
                className="inline-flex items-center px-8 py-3"
              >
                {getAdmissionFormStatusLabel(status, userType)}
              </ApplicationLink>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
              <p>{config?.description}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    },
  }

  const amountColumn: ColumnDef<TApplicationRow> = {
    accessorKey: 'requestedAmount',
    header: 'Valor Solicitado',
    meta: { width: '15%' },
    cell: ({ row }) => {
      const amount = row.original.requestedAmount
      return (
        <ApplicationLink row={row} getLink={getLink} className="inline-flex w-full px-8 py-3">
          <span className="font-semibold text-brand-grayscale-7">
            {amount ? formatBRLFromCents(amount) : '—'}
          </span>
        </ApplicationLink>
      )
    },
  }

  const createdAtColumn: ColumnDef<TApplicationRow> = {
    accessorKey: 'createdAt',
    meta: { width: '20%' },
    header: 'Data de Criação',
    cell: ({ row }) => (
      <ApplicationLink row={row} getLink={getLink} className="flex w-full px-8 py-3">
        <span>{row.original.createdAt ? formatDate(row.original.createdAt) : '—'}</span>
      </ApplicationLink>
    ),
  }

  const actionColumn: ColumnDef<TApplicationRow> = {
    id: 'action',
    header: 'Ações',
    meta: { width: '200px' },
    cell: ({ row }) => {
      const action = getStatusAction(row.original.status, userType)
      if (!action) return null

      if (action.kind === 'release-handoff') {
        return (
          <div className="px-4 py-3">
            <ReleaseHandoffButton
              applicationId={row.original.id}
              companyId={row.original.companyId}
              facilityId={row.original.facilityId}
              size="xs"
            />
          </div>
        )
      }

      const { to, params, search } = getLink(row, action.subpath)
      return (
        <div className="px-4 py-3">
          <Button asChild size="xs" className="whitespace-nowrap">
            <Link to={to} params={params} search={search}>
              {action.label}
              <ExternalLink className="ml-1.5 h-3 w-3" />
            </Link>
          </Button>
        </div>
      )
    },
  }

  return {
    nameColumn,
    phoneColumn,
    facilityColumn,
    statusColumn,
    amountColumn,
    createdAtColumn,
    actionColumn,
  }
}

export function getApplicationColumns({
  showFacility,
  userType,
  linkBuilder,
}: {
  showFacility: boolean
  userType: TUserType
  linkBuilder?: LinkBuilder
}): ColumnDef<TApplicationRow>[] {
  const getLink = linkBuilder ?? (showFacility ? staffLink : companyLink)
  const {
    nameColumn,
    phoneColumn,
    facilityColumn,
    statusColumn,
    amountColumn,
    createdAtColumn,
    actionColumn,
  } = buildColumns(getLink, userType)

  return [
    nameColumn,
    ...(showFacility ? [facilityColumn] : [phoneColumn]),
    statusColumn,
    amountColumn,
    createdAtColumn,
    actionColumn,
  ]
}
